import * as Sentry from '@sentry/capacitor';

import {
	createSepaPaymentMethodApi as createSepaPaymentMethodApiSDK,
	getDefaultPaymentMethodApi,
	IDefaultPaymentMethod,
	postApiCommandByCmdApi,
} from '@mopla/data-models';

import {
	ICreateSepaPaymentMethodParams,
	ISetPaymentMethodParams,
} from './types';

/**
 * Fetches the default payment method from the backend.
 * Returns null if no payment method exists (404).
 * Pure function - does not manage state.
 */
export const fetchPaymentMethodApi =
	async (): Promise<IDefaultPaymentMethod | null> => {
		try {
			/** This is not well typed in the openapi. */
			const { data } = await getDefaultPaymentMethodApi();
			const paymentMethod = data as unknown as IDefaultPaymentMethod;

			return paymentMethod;
		} catch (err: any) {
			/** 404 means no payment method exists - this is expected */
			if (err.response?.status === 404 || err.status === 404) {
				return null;
			}

			/** Unexpected error - log to Sentry */
			Sentry.captureException(err);
			throw err;
		}
	};

/**
 * Sets the default payment method on the backend.
 * Pure function - does not manage state.
 * Returns void on success, throws on error.
 */
export const setPaymentMethodApi = async (
	params: ISetPaymentMethodParams
): Promise<void> => {
	/** Sentry v9 performance tracking (from setDefaultPaymentMethodEffect) */
	return Sentry.startSpan(
		{
			name: 'setDefaultPaymentMethod',
			op: 'payment.method.set_default',
			attributes: {
				...(params.paymentMethodId && {
					paymentMethodId: params.paymentMethodId,
				}),
				...(params.setupIntentId && {
					setupIntentId: params.setupIntentId,
				}),
				hasPaymentMethodId: !!params.paymentMethodId,
				hasSetupIntentId: !!params.setupIntentId,
			},
		},
		async (span) => {
			try {
				const { paymentMethodId, setupIntentId } = params;

				if (paymentMethodId || setupIntentId) {
					await Sentry.startSpan(
						{
							name: 'setDefaultPaymentMethod.api_call',
							op: 'http.client',
							attributes: {
								'http.method': 'POST',
								'http.url': '/api/command/setDefaultPaymentMethod',
								'payment.has_method_id': !!paymentMethodId,
								'payment.has_setup_intent': !!setupIntentId,
							},
						},
						() =>
							postApiCommandByCmdApi({
								path: { cmd: 'setDefaultPaymentMethod' },
								body: {
									paymentMethodId: paymentMethodId ?? undefined,
									setupIntentId: setupIntentId ?? undefined,
								},
							})
					);
				} else {
					/**
					 * In case we didn't receive a callback, the payment method
					 * may still have been saved, so let the backend check if
					 * any payment method attach can become the default
					 */
					await Sentry.startSpan(
						{
							name: 'setDefaultPaymentMethod.api_call_fallback',
							op: 'http.client',
							attributes: {
								'http.method': 'POST',
								'http.url': '/api/command/setDefaultPaymentMethod',
								'payment.fallback_mode': true,
							},
						},
						() =>
							postApiCommandByCmdApi({
								path: { cmd: 'setDefaultPaymentMethod' },
								body: {},
							})
					);
				}

				span.setStatus({
					code: 1,
					message: 'Payment method set successfully',
				});
			} catch (error: any) {
				span.setStatus({
					code: 2,
					message: 'Failed to set payment method',
				});

				Sentry.captureException(error);
				throw error;
			}
		}
	);
};

/**
 * Deletes the default payment method.
 * Pure function - does not manage state.
 * Returns void on success, throws on error.
 */
export const deletePaymentMethodApi = async (): Promise<void> => {
	try {
		await postApiCommandByCmdApi({
			path: { cmd: 'deleteDefaultPaymentMethod' },
			body: {},
		});
	} catch (err: any) {
		Sentry.captureException(err);
		throw err;
	}
};

/**
 * Creates a SEPA payment method via TrueLayer.
 */
export const createSepaPaymentMethodApi = async (
	params: ICreateSepaPaymentMethodParams
): Promise<void> => {
	/** Sentry v9 performance tracking */
	return Sentry.startSpan(
		{
			name: 'createSepaPaymentMethod',
			op: 'payment.method.create_sepa',
			attributes: {
				hasIban: !!params.iban,
				hasEmail: !!params.email,
				country: params.address?.country,
			},
		},
		async (span) => {
			try {
				await Sentry.startSpan(
					{
						name: 'createSepaPaymentMethod.api_call',
						op: 'http.client',
						attributes: {
							'http.method': 'POST',
							'http.url': '/api/truelayer/createSepaPaymentMethod',
						},
					},
					() =>
						createSepaPaymentMethodApiSDK({
							body: {
								iban: params.iban,
								name: params.name,
								email: params.email,
								address: params.address,
							},
						})
				);

				span.setStatus({
					code: 1,
					message: 'SEPA payment method created successfully',
				});
			} catch (error: any) {
				span.setStatus({
					code: 2,
					message: 'Failed to create SEPA payment method',
				});

				Sentry.captureException(error);
				throw error;
			}
		}
	);
};
